import React from 'react';
import Axios from 'axios';
import LogRocket from 'logrocket';
import _ from 'lodash';
import MaskedInput from 'react-text-mask';
import { Form, Segment, TextArea, Input } from 'semantic-ui-react';
import Shift4Iframe from './shift4iframe';
import CartDetails from './CartDisplay';
import CheckoutMessage from './CheckoutMessage';

export default class CheckoutBody extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      firstName: '',
      lastName: '',
      email: '',
      note: '',
      phoneNumber: '',
      tip: 0.0,
      validatedCart: null,
      error: undefined,
      errors: [],
      loadingError: undefined,
      cartLoading: true,
      iframeLoading: true,
      paymentLoading: false,
      orderPlaced: false,
      success: false,
    };

    this.setLoading = this.setLoading.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleMaskedChange = this.handleMaskedChange.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.handleFailure = this.handleFailure.bind(this);
    this.handleCheckoutFailure = this.handleCheckoutFailure.bind(this);
    this.handleCartFailure = this.handleCartFailure.bind(this);
    this.handlePaymentSuccess = this.handlePaymentSuccess.bind(this);
    this.resetErrors = this.resetErrors.bind(this);
  }

  async componentDidMount() {
    if (process.env.NODE_ENV === 'production') {
      LogRocket.init('abqepz/skytab-takeout-websites');
    }

    try {
      const { data } = await this.getCartDetails();
      this.setState({ validatedCart: data });
    } catch (error) {
      LogRocket.captureException(error);
      this.handleCartFailure(error);
    } finally {
      this.setLoading('cartLoading', false);
    }
  }

  async getCartDetails() {
    const { cart, locationId } = this.props;
    const host = process.env.SERVER_URL || process.env.STORYBOOK_SERVER_URL;
    return Axios.post(`${host}ordering/cart/`, { locationId, ...cart });
  }

  setLoading(name, value) {
    this.setState({ [name]: value });
  }

  async submitCheckout(payment) {
    this.setLoading('paymentLoading', true);
    try {
      const { firstName, lastName, email, phoneNumber, tip, note, validatedCart } = this.state;
      const {
        locationId,
        cart: { clearCart },
      } = this.props;

      const data = {
        customer: {
          firstName,
          lastName,
          email,
          phoneNumber,
        },
        tip,
        payment,
        note,
        shopping_cart_id: validatedCart.cart.id,
        location_id: locationId,
      };

      const host = process.env.SERVER_URL || process.env.STORYBOOK_SERVER_URL;
      this.setLoading('paymentLoading', true);
      LogRocket.identify(`${firstName} ${lastName} @ ${locationId}/${data.shopping_cart_id}`);

      const response = await Axios.post(
        `${host}ordering/payments/locations/${locationId}/complete_sale/`,
        data,
      );
      if (response.status === 200) {
        this.handlePaymentSuccess();

        if (clearCart) {
          clearCart();
        }
      }
    } catch (error) {
      LogRocket.captureException(error);
      this.handleCheckoutFailure(error);
    } finally {
      this.setLoading('paymentLoading', false);
      this.setLoading('cartLoading', false);
    }
  }

  handlePaymentSuccess() {
    this.setState({ success: true });
  }

  handleCartFailure(error) {
    if (!error) return;
    const defaultError = 'There was an error loading your cart, please try again';
    const data = _.get(error, 'response.data');

    // If response.data is a string use it
    if (typeof data === 'string' && _.toLength(data)) {
      return this.setState({ loadingError: data, orderPlaced: false });
    }

    // If response.data.errors is a list, get the first error and use it
    const firstError = _.get(data, 'errors[0]');
    if (firstError && typeof firstError === 'string') {
      return this.setState({ loadingError: firstError, orderPlaced: false });
    }

    // If response.data.error is a string use it
    const loadingError = _.get(data, 'error');
    if (loadingError && typeof loadingError === 'string') {
      return this.setState({ loadingError, orderPlaced: false });
    }

    // Return default error
    return this.setState({ loadingError: defaultError, orderPlaced: false });
  }

  handleCheckoutFailure(error) {
    if (!error) return;
    const defaultError = 'There was an issue processing your payment';
    const data = _.get(error, 'response.data');

    // If response.data is a string use it
    if (typeof data === 'string' && _.toLength(data)) {
      return this.setState({ error, orderPlaced: false });
    }

    // If response.datae is an object check for known values and return error message
    if (typeof data === 'object') {
      if (
        Object.keys(data).includes('first_name') ||
        Object.keys(data).includes('last_name') ||
        Object.keys(data).includes('email') ||
        Object.keys(data).includes('phone_number')
      ) {
        const missingFields = Object.keys(data).map(err => `Missing ${err.replace('_', ' ')}`);
        return this.setState({
          error: 'Please provide the following contact information:',
          errors: missingFields,
          orderPlaced: false,
        });
      }
    }

    // If response.data.errors is a list, get the first error and use it
    const firstError = _.get(data, 'errors[0]');
    if (firstError && typeof firstError === 'string') {
      return this.setState({ error: firstError, orderPlaced: false });
    }

    // If response.data.error is a string use it
    const checkoutError = _.get(data, 'error');
    if (checkoutError && typeof checkoutError === 'string') {
      return this.setState({ error: checkoutError, orderPlaced: false });
    }

    // Return default error
    return this.setState({ error: defaultError, orderPlaced: false });
  }

  handleMaskedChange(e) {
    return this.handleChange(e, e.target);
  }

  handleChange(e, target) {
    const { value, id: name } = target;

    if (name === 'tip') {
      this.setState({ [name]: value ? Math.abs(parseFloat(value)) : null });
    } else {
      this.setState({ [name]: value });
    }
  }

  async handleSuccess(data) {
    const { orderPlaced, iframeLoading } = this.state;

    if (!orderPlaced && !iframeLoading) {
      this.setState({ orderPlaced: true }, () => this.submitCheckout(data));
    }
  }

  handleFailure(data) {
    let error = 'There was an issue processing your payment';

    if (data.i4go_responsetext) LogRocket.captureMessage(data.i4go_responsetext);
    if (data && data.i4go_responsetext) {
      error = data.i4go_responsetext;
    }

    return this.setState({ error, orderPlaced: false });
  }

  resetErrors() {
    this.setState({
      error: undefined,
      errors: [],
      loadingError: undefined,
    });
  }

  render() {
    const {
      firstName,
      lastName,
      email,
      phoneNumber,
      tip,
      note,
      error,
      errors,
      loadingError,
      success,
      cartLoading,
      paymentLoading,
      iframeLoading,
      validatedCart,
    } = this.state;
    const { locationId } = this.props;

    if (error) {
      return (
        <CheckoutMessage
          type="negative"
          primaryMessage="We could not submit your order."
          secondaryMessage={error}
          tertiaryMessages={errors}
          actionName="Retry"
          actionCallback={this.resetErrors}
        />
      );
    }

    if (loadingError) {
      return (
        <CheckoutMessage
          type="negative"
          primaryMessage="Error loading the cart."
          secondaryMessage={loadingError}
        />
      );
    }

    if (success) {
      return (
        <CheckoutMessage
          type="positive"
          primaryMessage="Your order has been submitted."
          secondaryMessage="Check your inbox for a confirmation email."
        />
      );
    }

    return (
      <Segment loading={cartLoading || paymentLoading || iframeLoading}>
        {validatedCart && (
          <CartDetails
            amounts={{ ...validatedCart.summary, tip }}
            handleChange={this.handleChange}
          />
        )}
        {!cartLoading && (
          <div>
            <h5 style={{ marginBottom: '0.5rem' }}>Notes</h5>
            <Form className="checkout-modal-notes">
              <Form.Group>
                <TextArea
                  style={{ margin: '0 0.5rem' }}
                  label="Notes"
                  placeholder="e.g. please include a fork or indicate car information if the restaurant allows curbside"
                  name="note"
                  id="note"
                  onChange={this.handleChange}
                  value={note}
                />
              </Form.Group>
            </Form>
            <h5 style={{ marginBottom: '0.5rem' }}>Contact Information</h5>
            <Form>
              <Form.Group widths="equal">
                <Form.Input
                  required
                  label="First Name"
                  type="text"
                  id="firstName"
                  placeholder="First Name"
                  onChange={this.handleChange}
                  value={firstName}
                />
                <Form.Input
                  required
                  label="Last Name"
                  type="text"
                  id="lastName"
                  placeholder="Last Name"
                  onChange={this.handleChange}
                  value={lastName}
                />
              </Form.Group>
              <Form.Group widths="equal">
                <Form.Input
                  required
                  label="Email"
                  type="email"
                  id="email"
                  placeholder="Email"
                  onChange={this.handleChange}
                  value={email}
                />
                <Form.Field required>
                  <label>Phone Number</label>
                  <Input>
                    <MaskedInput
                      mask={[
                        '(',
                        /[1-9]/,
                        /\d/,
                        /\d/,
                        ')',
                        ' ',
                        /\d/,
                        /\d/,
                        /\d/,
                        '-',
                        /\d/,
                        /\d/,
                        /\d/,
                        /\d/,
                      ]}
                      guide
                      placeholder="(123) 456-7890"
                      id="phoneNumber"
                      value={phoneNumber}
                      onChange={this.handleMaskedChange}
                      {...this.props}
                    />
                  </Input>
                </Form.Field>
              </Form.Group>
            </Form>
            <React.Fragment>
              <h5 style={{ marginBottom: '0.5rem' }}>Payment</h5>
              <Shift4Iframe
                locationId={locationId}
                handleSuccess={this.handleSuccess}
                handleFailure={this.handleFailure}
                handleLoadFailure={this.handleCartFailure}
                setLoading={value => this.setLoading('iframeLoading', value)}
              />
            </React.Fragment>
            <a
              href="https://www.shift4.com/pdf/Lighthouse_Terms-and-Conditions.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms and Conditions
            </a>
          </div>
        )}
      </Segment>
    );
  }
}
